import { Size } from '@usitsdasdesign/dds-react';
import Icon from 'atoms/Icon';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import "react-datepicker/dist/react-datepicker.css";
import InputMask from 'react-input-mask';
import PortalDatePicker from './PortalDatePicker';


const SIZE = {
    s: {
        label: '',
        input: 'text-xs placeholder:text-xs px-2 py-1',
        size: 'text-xs',
        icon: 'w-3.5 h-3.5',
        iconWrapper: 'w-[24px] h-[22px]',
    },
    m: {
        label: '',
        input: 'text-sm placeholder:text-sm px-3 py-[5px]',
        size: 'text-sm',
        icon: 'w-4 h-4',
        iconWrapper: 'w-[34px] h-[30px]',
    },
    l: {
        label: '',
        input: 'text-sm placeholder:text-sm px-4 py-[9px]',
        size: 'text-sm',
        icon: 'w-4 h-4',
        iconWrapper: 'w-[42px] h-[38px]',
    },
};

const DatePickerV2 = ({
    format = 'MM/DD/YYYY',
    value,
    dateSelected,
    isError,
    errorMessage = 'This is not a valid date',
    stickerOptions,
    icon = "calendar__s__stroke",
    label,
    isRequired,
    size = Size.m,
    maxDate,
    minDate,
    isDisabled = false,
    disabledDates,
    isManualInput = true,
    customClass,
    errorClass,
    validateDate = () => { },
    isValidatingManualDate = false,
    ...rest
}) => {
    const [position, setPosition] = useState({});
    const inputRef = useRef();
    const [calendarEl, setCalendarEl] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [manualDate, setManualDate] = useState('');
    const [isValid, setIsValid] = useState(true);

    useEffect(() => {
        if (value) {
            setManualDate(moment(value).format(format));
        } else {
            setManualDate('');
        }
    }, [value]);

    const onToggleDatePicker = () => setShowDatePicker(!showDatePicker);
    const onOpenDatePicker = () => setShowDatePicker(true);
    const onCloseDatePicker = () => setShowDatePicker(false);

    const handleScroll = () => {
        if (inputRef.current) {
            const { top, left, height } = inputRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            let dropdownHeight = 251;
            let style;
            if (calendarEl) {
                dropdownHeight = calendarEl?.getBoundingClientRect()?.height || dropdownHeight;
            } else {
                setCalendarEl(document.getElementById('dropdownContent'));
            }

            if ((top + height + dropdownHeight) < windowHeight) {
                style = {
                    transform: `translate(${left + 'px'}, ${top + 'px'})`,
                    top: height,
                    left: 0
                };
            } else {
                style = {
                    transform: `translate(${left + 'px'}, ${(top - dropdownHeight - 2) + 'px'})`,
                    top: 0,
                    left: 0,
                };
            }

            setPosition(style);
        }
    };


    useEffect(() => {
        handleScroll();
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showDatePicker]);

    const handleManualDateChange = (event) => {
        const date = event.target.value
        validateDate(date);
        if (date === '') {
            dateSelected(null)
            setManualDate('')
            setIsValid(true);
        } else {
            setManualDate(date);
            if (moment(date, format, true).isValid()) {
                setIsValid(true);
                dateSelected(date);
            } else {
                setIsValid(false);
            }
        }
    };

    useEffect(() => {
        if (isValidatingManualDate) {
            if (moment(manualDate, format, true).isValid()) {
                setIsValid(true);
            } else {
                setIsValid(false);
            }
        }
    }, [manualDate])

    const onKeyDown = (event) => {
        if (event.key === 'Enter') onOpenDatePicker();
        if (event.key === 'Tab') onCloseDatePicker();
    };

    return (
        <>
            <div className={`relative flex flex-col w-full ${customClass}`}>
                {label && (
                    <label className="text-gray-1100 text-xs mb-1">
                        {label}
                        {isRequired && <span className='text-red-900'>*</span>}
                    </label>
                )}
                <div
                    ref={inputRef}
                    className={`relative ${isDisabled ? 'pointer-events-none' : 'cursor-pointer'}`}
                    {...(!isDisabled && !isManualInput && { onClick: onToggleDatePicker })}
                >
                    <InputMask
                        mask="99/99/9999"
                        value={isManualInput ? manualDate : moment(value).format(format)}
                        onChange={handleManualDateChange}
                        maskChar={'_'}
                    >
                        {(inputProps) => (
                            <input
                                className={`w-full pr-9 border border-gray-200 font-normal ${isDisabled && 'opacity-40'} hover:border-gray-400 focus:border-black outline-none [&_input]:caret-transparent] placeholder:text-gray-1100 ${(isError || !isValid) && 'border-red-900'} ${SIZE[size].input}`}
                                type='text'
                                placeholder={format}
                                isDisabled={isDisabled}
                                onKeyDown={onKeyDown}
                                {...inputProps}
                            />
                        )}
                    </InputMask>
                    <div
                        className={`absolute right-[1px] top-[1px] flex items-center justify-center ${isDisabled ? 'opacity-60' : ''} ${SIZE[size].iconWrapper} ${isDisabled ? '' : 'hover:bg-[#ebebeb]'}`}
                        {...(!isDisabled && { onClick: onToggleDatePicker })}
                    >
                        <Icon icon={icon} size={SIZE[size].icon} />
                    </div>
                </div>
                {showDatePicker && (
                    <PortalDatePicker
                        setShowDatePicker={setShowDatePicker}
                        validValue={moment(isManualInput ? manualDate : value).isValid()}
                        value={moment(isManualInput ? manualDate : value).format(format)}
                        dateSelected={(date) => {
                            if (isManualInput) {
                                setManualDate(moment(date).format(format))
                            }
                            setIsValid(true);
                            dateSelected(date);
                        }}
                        maxDate={maxDate}
                        minDate={minDate}
                        // disabledDatesProps={{
                        //     minDate: moment(disabledDates?.before).isValid() ? moment(disabledDates?.before).format() : undefined,
                        //     maxDate: moment(disabledDates?.after).isValid() ? moment(disabledDates?.after).format() : undefined,
                        // }}
                        position={position}
                        onCloseDatePicker={onCloseDatePicker}
                        {...rest}
                    />
                )}
                {
                    (isError || !isValid) && (
                        <div className={`my-1 text-red-900 text-xs error-in-field ${errorClass}`}>{!isValid ? `Please enter date in format ${format}` : isError ? errorMessage : ''}</div>
                    )
                }
            </div>
        </>
    );
};

export default DatePickerV2;
